// 스케줄 슬롯 카드 컴포넌트
import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  Badge,
  Button,
  Flex,
  Stack,
  useToast,
  Tooltip,
  Tr,
  Td,
  ButtonGroup
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon, WarningIcon } from '@chakra-ui/icons';
import { useAuth } from 'contexts/AuthContext';
import {
  checkConflictAndApply,
  cancelScheduleApplication,
  getWorkSchedule
} from 'utils/firestoreUtils';
import { 
  formatScheduleDateTime, 
  checkScheduleConflict,
  calculateEstimatedWage
} from 'utils/scheduleUtils';

function ScheduleSlotCard({ 
  schedule: initialSchedule, 
  academicSchedule, 
  myApplications, 
  onUpdate,
  application = null,
  isMyApplication = false,
  isConfirmed = false,
  isTableView = false
}) {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [schedule, setSchedule] = useState(initialSchedule);

  useEffect(() => {
    // 신청 내역 탭에서 스케줄 정보 로드
    if (isMyApplication && application && !schedule) {
      loadSchedule();
    }
  }, [application, isMyApplication]);

  const loadSchedule = async () => {
    if (!application?.scheduleId) return;
    
    try {
      const scheduleData = await getWorkSchedule(application.scheduleId);
      setSchedule(scheduleData);
    } catch (error) {
      console.error('스케줄 조회 오류:', error);
    }
  };

  if (!schedule) {
    return (
      <Box p={4} borderWidth="1px" borderRadius="lg">
        <Text color="gray.500">스케줄 정보를 불러올 수 없습니다.</Text>
      </Box>
    );
  }

  // 충돌 체크
  const conflictResult = academicSchedule 
    ? checkScheduleConflict(academicSchedule, schedule)
    : { hasConflict: false, conflictingClasses: [] };

  // 내가 이미 신청했는지 확인
  const myApp = myApplications.find(app => 
    app.scheduleId === schedule.id && ['pending', 'selected'].includes(app.status)
  );

  // 신청 가능 여부
  const canApply = !myApp && !conflictResult.hasConflict && schedule.status === 'open';

  const handleApply = async () => {
    try {
      setLoading(true);
      await checkConflictAndApply(currentUser.uid, schedule.id, '');
      
      toast({
        title: '신청 완료',
        description: '스케줄 신청이 완료되었습니다.',
        status: 'success',
        duration: 3000
      });
      
      onUpdate();
    } catch (error) {
      console.error('신청 오류:', error);
      toast({
        title: '신청 실패',
        description: error.message || '스케줄 신청에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!myApp) return;
    
    if (!window.confirm('신청을 취소하시겠습니까?')) {
      return;
    }

    try {
      setLoading(true);
      await cancelScheduleApplication(myApp.id);
      
      toast({
        title: '취소 완료',
        description: '신청이 취소되었습니다.',
        status: 'success',
        duration: 3000
      });
      
      onUpdate();
    } catch (error) {
      console.error('취소 오류:', error);
      toast({
        title: '취소 실패',
        description: '신청 취소에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const estimatedWage = calculateEstimatedWage(
    schedule.wageAmount,
    schedule.startTime,
    schedule.endTime
  );

  const getStatusBadge = () => {
    if (isConfirmed || (application && application.status === 'selected')) {
      return <Badge colorScheme="green">선정</Badge>;
    }
    if (application && application.status === 'pending') {
      return <Badge colorScheme="yellow">대기중</Badge>;
    }
    if (application && application.status === 'rejected') {
      return <Badge colorScheme="red">미선정</Badge>;
    }
    if (application && application.status === 'canceled') {
      return <Badge colorScheme="gray">취소됨</Badge>;
    }
    if (schedule.status === 'closed') {
      return <Badge colorScheme="gray">마감</Badge>;
    }
    if (schedule.status === 'open') {
      return <Badge colorScheme="blue">모집중</Badge>;
    }
    return <Badge colorScheme="gray">완료</Badge>;
  };

  // 테이블 뷰
  if (isTableView) {
    return (
      <Tr bg={conflictResult.hasConflict ? 'red.50' : 'white'}>
        <Td>{schedule.workDate}</Td>
        <Td fontSize="sm">{schedule.startTime} - {schedule.endTime}</Td>
        <Td fontWeight="bold">{schedule.location}</Td>
        <Td>
          <Badge colorScheme="purple">
            {schedule.confirmedCount || 0} / {schedule.requiredHeadcount}
          </Badge>
        </Td>
        <Td>{schedule.wageAmount?.toLocaleString()}원</Td>
        <Td fontWeight="bold">{estimatedWage.toLocaleString()}원</Td>
        <Td>
          <Flex gap={2} align="center">
            {getStatusBadge()}
            {conflictResult.hasConflict && (
              <Tooltip label="학업시간 충돌">
                <WarningIcon color="red.500" />
              </Tooltip>
            )}
          </Flex>
        </Td>
        <Td>
          {!isConfirmed && !isMyApplication && (
            <>
              {myApp ? (
                <>
                  {myApp.status === 'pending' && (
                    <Button
                      size="xs"
                      colorScheme="red"
                      onClick={handleCancel}
                      isLoading={loading}
                    >
                      취소
                    </Button>
                  )}
                </>
              ) : (
                <Tooltip
                  label={
                    conflictResult.hasConflict 
                      ? '학업시간과 겹침' 
                      : schedule.status !== 'open'
                      ? '마감됨'
                      : ''
                  }
                  isDisabled={canApply}
                >
                  <Button
                    size="xs"
                    colorScheme="blue"
                    onClick={handleApply}
                    isLoading={loading}
                    isDisabled={!canApply}
                  >
                    신청
                  </Button>
                </Tooltip>
              )}
            </>
          )}
          {isMyApplication && application && application.status === 'pending' && (
            <Button
              size="xs"
              colorScheme="red"
              onClick={handleCancel}
              isLoading={loading}
            >
              취소
            </Button>
          )}
        </Td>
      </Tr>
    );
  }

  // 카드 뷰
  return (
    <Box
      p={4}
      borderWidth="1px"
      borderRadius="lg"
      borderColor={conflictResult.hasConflict ? 'red.200' : 'gray.200'}
      bg={conflictResult.hasConflict ? 'red.50' : 'white'}
      position="relative"
    >
      <Stack spacing={3}>
        <Flex justify="space-between" align="start">
          <Text fontSize="lg" fontWeight="bold">
            {schedule.location}
          </Text>
          {getStatusBadge()}
        </Flex>

        <Text fontSize="sm" color="gray.600">
          {formatScheduleDateTime(schedule.workDate, schedule.startTime, schedule.endTime)}
        </Text>

        <Flex gap={2} flexWrap="wrap">
          <Badge colorScheme="purple">
            {schedule.confirmedCount || 0} / {schedule.requiredHeadcount}명
          </Badge>
          <Badge colorScheme="orange">
            시급 {schedule.wageAmount?.toLocaleString()}원
          </Badge>
          <Badge colorScheme="teal">
            예상 {estimatedWage.toLocaleString()}원
          </Badge>
        </Flex>

        {schedule.description && (
          <Text fontSize="sm" color="gray.700">
            {schedule.description}
          </Text>
        )}

        {conflictResult.hasConflict && (
          <Box p={2} bg="red.100" borderRadius="md">
            <Flex align="center" gap={2} mb={1}>
              <WarningIcon color="red.600" />
              <Text fontSize="sm" fontWeight="bold" color="red.700">
                학업시간 충돌
              </Text>
            </Flex>
            {conflictResult.conflictingClasses.map((cls, idx) => (
              <Text key={idx} fontSize="xs" color="red.600">
                • {cls.subject} ({cls.time})
              </Text>
            ))}
          </Box>
        )}

        {/* 버튼 영역 */}
        {!isConfirmed && !isMyApplication && (
          <Box>
            {myApp ? (
              <Flex gap={2}>
                {myApp.status === 'pending' && (
                  <Button
                    size="sm"
                    colorScheme="red"
                    leftIcon={<CloseIcon />}
                    onClick={handleCancel}
                    isLoading={loading}
                    flex={1}
                  >
                    신청 취소
                  </Button>
                )}
                {myApp.status === 'selected' && (
                  <Badge colorScheme="green" fontSize="md" p={2}>
                    선정되었습니다!
                  </Badge>
                )}
              </Flex>
            ) : (
              <Tooltip
                label={
                  conflictResult.hasConflict 
                    ? '학업시간과 겹쳐 신청할 수 없습니다.' 
                    : schedule.status !== 'open'
                    ? '마감된 스케줄입니다.'
                    : ''
                }
                isDisabled={canApply}
              >
                <Button
                  colorScheme="blue"
                  leftIcon={<CheckIcon />}
                  onClick={handleApply}
                  isLoading={loading}
                  isDisabled={!canApply}
                  width="100%"
                >
                  신청하기
                </Button>
              </Tooltip>
            )}
          </Box>
        )}

        {isMyApplication && application && (
          <Box>
            {application.status === 'pending' && (
              <Button
                size="sm"
                colorScheme="red"
                leftIcon={<CloseIcon />}
                onClick={handleCancel}
                isLoading={loading}
                width="100%"
              >
                신청 취소
              </Button>
            )}
            {application.status === 'rejected' && application.adminNote && (
              <Box p={2} bg="gray.100" borderRadius="md">
                <Text fontSize="xs" color="gray.600">
                  거절 사유: {application.adminNote}
                </Text>
              </Box>
            )}
          </Box>
        )}

        {isConfirmed && (
          <Badge colorScheme="green" fontSize="md" p={2} textAlign="center">
            확정된 근무입니다
          </Badge>
        )}
      </Stack>
    </Box>
  );
}

export default ScheduleSlotCard;
